import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { loadImage } from '@/utils/mockupLayers';
import {
  FRONT_LEFT_CHEST,
  MAX_EMBROIDERY_WIDTH_MM,
  MIN_EMBROIDERY_WIDTH_MM,
  createPrintContext,
  getPlacementPreset,
  isValidPresetId,
  mm,
  presetToRelativeDesignState,
  type PaletteEntry,
  type PlacementContext,
  type PrintContext,
  type RelativeDesignState,
} from '@designdeck/shared';

/**
 * State for the guided creator entry (motif first, products second).
 *
 * Lives outside the step component because the seller can walk back from the
 * product catalogue into step 1 — component-local state would drop the motif
 * on every such round trip.
 *
 * Holds authoring intent only: which asset, which preset, which width. The
 * canvas state it produces is written into canvasStateManager on commit; this
 * store never owns design geometry.
 */

/** The two sides the guided entry offers. */
export type GuidedSide = 'front' | 'back';

/**
 * Print and embroidery run separately — a print run carries up to two motifs
 * across front and back, an embroidery run one motif stitched into the
 * product's embroidery field. Wanting both means two runs (source design).
 */
export type GuidedProcedure = 'print' | 'embroidery';

/** Placement chosen for a side: a preset id, or free placement on the canvas. */
export const CUSTOM_PLACEMENT = 'custom' as const;
/**
 * Placement of an embroidery motif: centred in the product's embroidery
 * field, sized by the seller within it. Not a registry preset — the field is
 * the position's own Print Area, so there is nothing to anchor against.
 */
export const EMBROIDERY_PLACEMENT = 'embroidery' as const;
export type GuidedPlacement =
  | string
  | typeof CUSTOM_PLACEMENT
  | typeof EMBROIDERY_PLACEMENT;

/** An uploaded motif, already persisted — never a pending upload. */
export interface GuidedMotif {
  /** Asset id from the upload response. */
  assetId: string;
  /** Permanent URL. Placement only happens after the upload resolved, so no
   *  `temp_` asset ever reaches the canvas from this flow. */
  url: string;
  /** Natural pixel dimensions, needed for designRatio and preset fitting. */
  width: number;
  height: number;
  fileName: string;
}

/**
 * An embroidery motif on its way to becoming a design: analysed, and then
 * confirmed with a thread mapping. Only the confirmed motif is a design — its
 * stitch render becomes the side's `motif` and the production artwork.
 */
export interface GuidedEmbroidery {
  /** Storage key of the uploaded source; reopens the mapping after a reload. */
  embFileKey: string;
  /** Posterized preview from the analysis, shown until the threads are confirmed. */
  previewUrl: string;
  /** Blob URL of the upload for the render; null once the session is gone. */
  sourceUrl: string | null;
  /** Dominant design colours; empty until analysed (or re-analysed after a reload). */
  palette: PaletteEntry[];
  /** Natural size of the uploaded source. */
  width: number;
  height: number;
  fileName: string;
  /** Design hex -> thread id, once confirmed. */
  mapping: Record<string, string> | null;
}

/**
 * The product the Creator preselected for this visit, as CreatorPage resolved
 * it from `?product=` or `?SKU=`. `productId: null` means resolved, but no
 * product from the URL is selected (none named, the fallback default, or a
 * name that does not exist).
 */
export type GuidedPreselection =
  | { productId: string; source: 'product' | 'sku' }
  | { productId: null };

export interface GuidedSideState {
  motif: GuidedMotif | null;
  placement: GuidedPlacement;
  /**
   * Seller-set width as a fraction of the print area, overriding what the
   * preset would fit.
   *
   * Relative rather than millimetres because the Creator treats geometry as the
   * truth and derives the physical size from it via pxPerMm — see
   * components/creator/guidedSize.ts. Storing mm here would make the same
   * placement mean different things on different print areas.
   */
  relWidthOverride: number | null;
  /** Embroidery run only: the motif's analysis and thread mapping. */
  embroidery: GuidedEmbroidery | null;
}

const DEFAULT_FRONT_PRESET = 'front_center_large';
const DEFAULT_BACK_PRESET = 'back_center_large';

function defaultPlacement(side: GuidedSide, procedure: GuidedProcedure | null) {
  if (procedure === 'embroidery') return EMBROIDERY_PLACEMENT;
  return side === 'front' ? DEFAULT_FRONT_PRESET : DEFAULT_BACK_PRESET;
}

function emptySide(placement: GuidedPlacement): GuidedSideState {
  return {
    motif: null,
    placement,
    relWidthOverride: null,
    embroidery: null,
  };
}

/**
 * Whether the seller has work on either side that a procedure switch would
 * discard: a motif, or an embroidery upload that is analysed but not
 * confirmed yet.
 */
export function hasGuidedWork(
  state: Pick<GuidedEntryState, 'front' | 'back'>
): boolean {
  return [state.front, state.back].some(
    (side) => !!side.motif || !!side.embroidery
  );
}

/**
 * Release the blob URL of an embroidery source that is being dropped —
 * otherwise every upload keeps its original file in memory for the session.
 * Revoking an already-revoked URL is a no-op, so the thread store may do the
 * same for the URL it shares.
 */
function releaseSource(side: GuidedSideState | undefined) {
  const url = side?.embroidery?.sourceUrl;
  if (
    url &&
    url.startsWith('blob:') &&
    typeof URL.revokeObjectURL === 'function'
  ) {
    URL.revokeObjectURL(url);
  }
}

interface GuidedEntryState {
  /**
   * The run the seller chose. Null on a fresh visit: the step then asks
   * "Druck oder Stick?" before it shows any card. A loaded design brings
   * its run along.
   */
  procedure: GuidedProcedure | null;
  /**
   * The initial product selection of this visit; null until CreatorPage has
   * resolved it. Step 1 reads it for the preselection hint and the start
   * procedure, so both agree with what step 2 has ticked.
   */
  preselection: GuidedPreselection | null;
  setPreselection: (preselection: GuidedPreselection) => void;
  front: GuidedSideState;
  back: GuidedSideState;

  /**
   * Side currently being placed on the canvas, or null.
   *
   * While set, the guided surface steps aside for the normal editor, so the
   * seller moves, scales and rotates with the controls they already know.
   */
  editingSide: GuidedSide | null;
  /**
   * The product whose Print Areas sizes and fit the motifs, pinned when the
   * step first resolves one. Step 2 changes the focused product on every
   * card click; following it live would silently re-fit a millimetre preset
   * against a different area and carry the new relative width everywhere.
   */
  referenceProductId: string | null;
  setReferenceProductId: (id: string | null) => void;
  beginCustomPlacement: (side: GuidedSide) => void;
  endCustomPlacement: () => void;

  /**
   * Switch between the print and the embroidery run. Both sides start over:
   * a print motif is not an embroidery artwork and the other way round, so
   * carrying one across would present it as something it is not.
   */
  setProcedure: (procedure: GuidedProcedure) => void;

  setMotif: (side: GuidedSide, motif: GuidedMotif) => void;
  clearMotif: (side: GuidedSide) => void;
  setPlacement: (side: GuidedSide, placement: GuidedPlacement) => void;
  setRelWidthOverride: (side: GuidedSide, relWidth: number | null) => void;

  /** An analysed embroidery upload, waiting for its thread mapping. */
  setEmbroideryPending: (
    side: GuidedSide,
    embroidery: Omit<GuidedEmbroidery, 'mapping'>
  ) => void;
  /** Palette (and a fresh source URL) for a motif reopened after a reload. */
  setEmbroideryPalette: (
    side: GuidedSide,
    palette: PaletteEntry[],
    sourceUrl?: string | null
  ) => void;
  /** The threads are chosen and the stitch render is stored: now it is a motif. */
  confirmEmbroidery: (
    side: GuidedSide,
    confirmed: { motif: GuidedMotif; mapping: Record<string, string> }
  ) => void;
  /** An embroidery upload that has not been confirmed yet — not a design. */
  hasUnconfirmedEmbroidery: () => boolean;

  /**
   * Seed the surface from a guided design that was saved earlier.
   *
   * The motif's natural size is read from the stored crop so presets fit and
   * the DPI label is honest; a crop that will not load leaves its side empty.
   * A preset id the registry no longer knows falls back to free placement.
   * A side whose object carries an `embFileKey` was embroidered; the whole
   * design is then an embroidery run.
   */
  hydrateFromDesign: (
    sides: Array<{
      side: GuidedSide;
      cropUrl: string;
      placementPresetId: string | null;
      /** Width in mm of the stored placement (DesignPosition.placementMm). */
      designWidthMm: number | null;
      /** Physical width of the Print Area that is the reference after loading. */
      referenceWidthMm: number | null;
      /** Storage key on the stored object when the motif is embroidered. */
      embFileKey?: string | null;
      /** The confirmed thread mapping (DesignPosition.threadMapping). */
      threadMapping?: Record<string, string> | null;
    }>,
    /** The loaded design's master product — the reference from now on. */
    referenceProductId?: string | null
  ) => Promise<void>;

  /** Sides that carry a motif, in commit order. */
  filledSides: () => GuidedSide[];
  /** True when at least one side can be committed. */
  canContinue: () => boolean;
  reset: () => void;
}

/**
 * Bumped by reset() and by every hydrate. A hydrate that finds a newer
 * generation after its image loads belongs to a design no longer open and
 * must not write.
 */
let hydrateGeneration = 0;

export const useGuidedEntryStore = create<GuidedEntryState>()(
  devtools(
    (set, get) => ({
      procedure: null,
      preselection: null,
      front: emptySide(DEFAULT_FRONT_PRESET),
      back: emptySide(DEFAULT_BACK_PRESET),
      editingSide: null,
      referenceProductId: null,

      setPreselection: (preselection) => set({ preselection }),

      setReferenceProductId: (id) => set({ referenceProductId: id }),

      beginCustomPlacement: (side) =>
        set((state) => ({
          editingSide: side,
          [side]: { ...state[side], placement: CUSTOM_PLACEMENT },
        })),

      endCustomPlacement: () => set({ editingSide: null }),

      setProcedure: (procedure) =>
        set((state) => {
          if (state.procedure === procedure) return {};
          releaseSource(state.front);
          releaseSource(state.back);
          return {
            procedure,
            front: emptySide(defaultPlacement('front', procedure)),
            back: emptySide(defaultPlacement('back', procedure)),
            editingSide: null,
          };
        }),

      hydrateFromDesign: async (sides, referenceProductId = null) => {
        const generation = ++hydrateGeneration;
        const loaded = await Promise.all(
          sides.map(async (entry) => {
            try {
              const img = await loadImage(entry.cropUrl);
              return {
                entry,
                width: img.naturalWidth,
                height: img.naturalHeight,
              };
            } catch {
              return null;
            }
          })
        );
        // A reset() or a newer load happened while the crops were loading —
        // this hydrate belongs to a design that is no longer open.
        if (generation !== hydrateGeneration) return;
        set((state) => {
          releaseSource(state.front);
          releaseSource(state.back);
          const procedure: GuidedProcedure = sides.some(
            (entry) => !!entry.embFileKey
          )
            ? 'embroidery'
            : 'print';
          // The loaded design is authoritative for both sides: a side it does
          // not carry, or whose crop failed to load, ends empty — never with
          // what a previously opened design left in memory.
          const next: Partial<GuidedEntryState> = {
            procedure,
            editingSide: null,
            referenceProductId: referenceProductId ?? state.referenceProductId,
            front: emptySide(defaultPlacement('front', procedure)),
            back: emptySide(defaultPlacement('back', procedure)),
          };
          for (const item of loaded) {
            if (!item || !(item.width > 0) || !(item.height > 0)) continue;
            const { entry } = item;
            const fileName = entry.cropUrl.split('/').pop() ?? 'motiv';
            const motif: GuidedMotif = {
              assetId: '',
              url: entry.cropUrl,
              width: item.width,
              height: item.height,
              fileName,
            };
            // The seller saw millimetres, so millimetres come back. After a
            // save the master product is the reference, and its Print Area
            // may be narrower or wider than the one the design was made on.
            const relWidthFromMm =
              entry.designWidthMm && entry.referenceWidthMm
                ? Math.min(entry.designWidthMm / entry.referenceWidthMm, 1)
                : null;

            if (entry.embFileKey) {
              // The stored crop is the stitch render; the palette is read
              // back from the source when the seller reopens the threads.
              // Without a stored mapping the artwork was never confirmed:
              // it is an open upload again, not a design — the sync would
              // otherwise rewrite it as a print motif on the next save.
              next[entry.side] = {
                motif: entry.threadMapping ? motif : null,
                placement: EMBROIDERY_PLACEMENT,
                relWidthOverride: relWidthFromMm,
                embroidery: {
                  embFileKey: entry.embFileKey,
                  previewUrl: entry.cropUrl,
                  sourceUrl: null,
                  palette: [],
                  width: item.width,
                  height: item.height,
                  fileName,
                  mapping: entry.threadMapping ?? null,
                },
              };
              continue;
            }

            const preset =
              entry.placementPresetId &&
              isValidPresetId(entry.placementPresetId)
                ? entry.placementPresetId
                : CUSTOM_PLACEMENT;
            next[entry.side] = {
              motif,
              placement: preset,
              relWidthOverride:
                preset === CUSTOM_PLACEMENT ? null : relWidthFromMm,
              embroidery: null,
            };
          }
          return next;
        });
      },

      setMotif: (side, motif) =>
        set((state) => ({
          [side]: {
            ...state[side],
            motif,
            // A new motif invalidates a width chosen against the previous one's
            // aspect ratio.
            relWidthOverride: null,
          },
        })),

      clearMotif: (side) =>
        set((state) => ({
          [side]:
            (releaseSource(state[side]),
            emptySide(defaultPlacement(side, state.procedure))),
          // Without a motif there is nothing to place, so an editor mounted on
          // this side would sit over an empty frame.
          editingSide: state.editingSide === side ? null : state.editingSide,
        })),

      setPlacement: (side, placement) =>
        set((state) => ({
          // A placement brings its own size: a width typed or slid for the
          // previous one would otherwise silently override the preset the
          // seller just chose.
          [side]: { ...state[side], placement, relWidthOverride: null },
          // Choosing a preset ends free placement for that side, so the frame
          // goes back to a still preview instead of keeping an editor mounted.
          editingSide:
            placement === CUSTOM_PLACEMENT
              ? state.editingSide
              : state.editingSide === side
                ? null
                : state.editingSide,
        })),

      setRelWidthOverride: (side, relWidthOverride) =>
        set((state) => ({ [side]: { ...state[side], relWidthOverride } })),

      setEmbroideryPending: (side, embroidery) =>
        set((state) => ({
          [side]:
            (releaseSource(state[side]),
            {
              motif: null,
              placement: EMBROIDERY_PLACEMENT,
              relWidthOverride: null,
              embroidery: { ...embroidery, mapping: null },
            }),
        })),

      setEmbroideryPalette: (side, palette, sourceUrl) =>
        set((state) => {
          const current = state[side].embroidery;
          if (!current) return {};
          return {
            [side]: {
              ...state[side],
              embroidery: {
                ...current,
                palette,
                sourceUrl:
                  sourceUrl === undefined ? current.sourceUrl : sourceUrl,
              },
            },
          };
        }),

      confirmEmbroidery: (side, { motif, mapping }) =>
        set((state) => {
          const current = state[side].embroidery;
          if (!current) return {};
          return {
            [side]: {
              ...state[side],
              motif,
              embroidery: { ...current, mapping },
            },
          };
        }),

      hasUnconfirmedEmbroidery: () => {
        const { front, back } = get();
        return [front, back].some((side) => !!side.embroidery && !side.motif);
      },

      filledSides: () => {
        const { front, back } = get();
        const sides: GuidedSide[] = [];
        if (front.motif) sides.push('front');
        if (back.motif) sides.push('back');
        return sides;
      },

      canContinue: () => get().filledSides().length > 0,

      reset: () => {
        hydrateGeneration += 1;
        releaseSource(get().front);
        releaseSource(get().back);
        set({
          procedure: null,
          preselection: null,
          referenceProductId: null,
          front: emptySide(DEFAULT_FRONT_PRESET),
          back: emptySide(DEFAULT_BACK_PRESET),
          editingSide: null,
        });
      },
    }),
    { name: 'GuidedEntryStore' }
  )
);

/**
 * Largest relative width an embroidery motif can take in the field without
 * leaving it on either axis — the slider's ceiling.
 */
export function embroideryMaxRelWidth(
  motif: { width: number; height: number },
  ctx: PlacementContext
): number {
  const ratio = motif.height / motif.width;
  const areaRatio = ctx.heightPx / ctx.widthPx;
  const inField = ratio <= areaRatio ? 1 : areaRatio / ratio;
  // The machines stitch at most MAX_EMBROIDERY_WIDTH_MM wide, whatever the
  // field allows. Without a scale the field is the only known bound.
  if (!(ctx.pxPerMm > 0)) return inField;
  const machineLimit = (MAX_EMBROIDERY_WIDTH_MM * ctx.pxPerMm) / ctx.widthPx;
  return Math.min(inField, machineLimit);
}

/**
 * Narrowest relative width an embroidered motif may take: the minimum in mm,
 * unless the field (or the machine) does not even allow that much.
 */
export function embroideryMinRelWidth(
  ctx: PlacementContext,
  maxRelWidth: number
): number {
  if (!(ctx.pxPerMm > 0)) return maxRelWidth;
  return Math.min(
    maxRelWidth,
    (MIN_EMBROIDERY_WIDTH_MM * ctx.pxPerMm) / ctx.widthPx
  );
}

/**
 * The embroidery field as the seller sees it: the product's Print Area cut
 * down to the machine's 9 × 9 cm, centred. The frame shows this field, so a
 * 9 cm motif fills it instead of sitting small in a 38 cm print area. Canvas
 * state stays relative to the Print Area — the field is a view, not a
 * coordinate system; convert with the ratio of the two widths.
 */
export function embroideryFieldContext(area: PrintContext): PrintContext {
  if (!(area.pxPerMm > 0)) return area;
  const limitPx = MAX_EMBROIDERY_WIDTH_MM * area.pxPerMm;
  const widthPx = Math.min(area.widthPx, limitPx);
  const heightPx = Math.min(area.heightPx, limitPx);
  if (widthPx === area.widthPx && heightPx === area.heightPx) return area;
  return createPrintContext({
    widthPx,
    heightPx,
    widthMm: widthPx / area.pxPerMm,
    heightMm: heightPx / area.pxPerMm,
    pxPerMm: area.pxPerMm,
    offsetX: 0,
    offsetY: 0,
    mockupWidthPx: widthPx,
    mockupHeightPx: heightPx,
  });
}

/**
 * Normalized placement for one side, ready for `writePositionFromPlacement`.
 *
 * A custom placement is returned as drawn. A preset is fitted against the given
 * print area, so the same preset yields a different relative width on a
 * narrower area — which is what `carryOverPlacement` then carries to the other
 * products. An embroidery motif sits centred in the field, at the seller's
 * width or the default, never larger than the field.
 *
 * Returns null when the side has no motif, or when its preset id is unknown —
 * an unknown id must not silently resolve to a different placement.
 */
export function resolveGuidedPlacement(
  side: GuidedSideState,
  ctx: PlacementContext
): RelativeDesignState | null {
  if (!side.motif) return null;

  if (side.placement === CUSTOM_PLACEMENT) {
    // The canvas owns a custom placement: its JSON carries the rotation and the
    // exact transform, neither of which survives a round trip through
    // RelativeDesignState. Nothing to resolve here.
    return null;
  }

  if (side.placement === EMBROIDERY_PLACEMENT) {
    // As large as the field and the machine allow by default (9 cm on any
    // field that has the room), and never narrower than the minimum.
    const max = embroideryMaxRelWidth(side.motif, ctx);
    const min = embroideryMinRelWidth(ctx, max);
    const relWidth =
      side.relWidthOverride === null
        ? max
        : Math.min(Math.max(side.relWidthOverride, min), max);
    // A stitched logo sits on the left chest on every product — the frame
    // shows the field centred, the placement itself takes the anchor of the
    // print preset of that name. The preset's box is set to the chosen size,
    // so its clamp keeps the motif inside the area at exactly that width.
    const widthMm = (relWidth * ctx.widthPx) / ctx.pxPerMm;
    const anchored = presetToRelativeDesignState(
      {
        ...FRONT_LEFT_CHEST,
        maxWidth: mm(widthMm),
        maxHeight: mm((widthMm * side.motif.height) / side.motif.width),
      },
      side.motif.width,
      side.motif.height,
      ctx
    );
    return { ...anchored, relWidth };
  }

  if (!isValidPresetId(side.placement)) return null;

  const fitted = presetToRelativeDesignState(
    getPlacementPreset(side.placement),
    side.motif.width,
    side.motif.height,
    ctx
  );

  if (side.relWidthOverride === null) return fitted;

  // The seller resized: keep the preset's anchor, take their width -- but
  // never taller than the area allows around that anchor (R12). A tall
  // motif at the maximum width used to poke over the top edge by ~1 %,
  // which the carry-over then read as a deliberate overflow.
  const cap = presetMaxRelWidthForHeight(side, ctx);
  return { ...fitted, relWidth: Math.min(side.relWidthOverride, cap) };
}

/**
 * The widest a preset placement may be before its height overshoots the
 * print area around the preset's anchor: the height is bounded by twice the
 * distance from the anchor's centre to the nearer edge. 1 (the area width)
 * when the side has no motif or no preset.
 */
export function presetMaxRelWidthForHeight(
  side: GuidedSideState,
  ctx: PlacementContext
): number {
  if (!side.motif || !isValidPresetId(side.placement)) return 1;
  const fitted = presetToRelativeDesignState(
    getPlacementPreset(side.placement),
    side.motif.width,
    side.motif.height,
    ctx
  );
  const ratio = side.motif.height / side.motif.width;
  if (!(ratio > 0) || !(ctx.widthPx > 0) || !(ctx.heightPx > 0)) return 1;
  const half = Math.min(fitted.relCenterY, 1 - fitted.relCenterY);
  const maxRelHeight = Math.max(0, 2 * half);
  const maxRelWidth = (maxRelHeight * ctx.heightPx) / (ratio * ctx.widthPx);
  return Math.min(1, maxRelWidth);
}
